import { decodeConfigConnectionTokenV2 } from '@secureblob/sdk/workspaceSecretV2'
import type { NormalizedAgentMcpServerConfig } from '../agentTypes'

export interface McpServerMetrics {
    callCount: number
    errorCount: number
    latencyMsP50: number
    latencyMsP99: number
    inflight: number
    activeSubscriptions: number
}

export interface McpToolCallEntry {
    toolName: string
    status: 'Succeeded' | 'Failed' | 'Cancelled'
    startedAtUtc: string
    completedAtUtc: string
    durationMs: number
    errorCode?: string | null
    errorMessage?: string | null
    requestSummary?: string | null
    responseSummary?: string | null
}

interface McpStatusToolSnapshot {
    Calls?: number
    Errors?: number
    LatencyP50Ms?: number
    LatencyP95Ms?: number
    LatencyP99Ms?: number
    calls?: number
    errors?: number
    latencyP50Ms?: number
    latencyP95Ms?: number
    latencyP99Ms?: number
}

export interface McpStatusSnapshot {
    serverName: string
    serverVersion: string
    uptimeSeconds: number
    inflight: number
    activeWorkspaceEventSubscriptions: number
    tools: Record<string, McpStatusToolSnapshot>
    recentToolCalls?: McpToolCallEntry[]
}

export interface McpServerStatusDetails {
    metrics: McpServerMetrics
    recentToolCalls: McpToolCallEntry[]
}

function getStatusBaseOrigin(): string {
    if (typeof window !== 'undefined' && typeof window.location?.origin === 'string') {
        return window.location.origin
    }
    if (typeof location !== 'undefined' && typeof location.origin === 'string') {
        return location.origin
    }
    return 'http://localhost'
}

function getHeaderValue(headers: Record<string, string> | undefined, headerName: string): string | undefined {
    if (!headers) return undefined
    const match = Object.entries(headers).find(([name]) => name.toLowerCase() === headerName.toLowerCase())
    return typeof match?.[1] === 'string' ? match[1] : undefined
}

function hasSecureBlobConfigToken(headers: Record<string, string> | undefined): boolean {
    const directToken = getHeaderValue(headers, 'X-SecureBlob-Key')?.trim()
    if (directToken && decodeConfigConnectionTokenV2(directToken)) {
        return true
    }

    const authorizationHeader = getHeaderValue(headers, 'Authorization')?.trim()
    const bearerMatch = authorizationHeader?.match(/^Bearer\s+(.+)$/i)
    const bearerToken = bearerMatch?.[1]?.trim()
    return !!(bearerToken && decodeConfigConnectionTokenV2(bearerToken))
}

export function getSecureBlobStatusHeaders(headers: Record<string, string> | undefined): Record<string, string> | null {
    if (!headers || !hasSecureBlobConfigToken(headers)) {
        return null
    }

    return getHeaderValue(headers, 'Accept')
        ? { ...headers }
        : { ...headers, Accept: 'application/json' }
}

export function buildMcpStatusUrl(
    server: Pick<NormalizedAgentMcpServerConfig, 'url' | 'upstreamUrl'>,
    baseOrigin = getStatusBaseOrigin(),
): string | null {
    const endpointUrl = server.upstreamUrl ?? server.url
    if (!endpointUrl) return null

    let parsed: URL
    try {
        parsed = new URL(endpointUrl, baseOrigin)
    } catch {
        return null
    }

    const trimmedPath = parsed.pathname.replace(/\/+$/, '')
    const oauthMarker = '/mcp/oauth/'
    const oauthIndex = trimmedPath.indexOf(oauthMarker)

    if (oauthIndex >= 0) {
        parsed.pathname = `${trimmedPath.slice(0, oauthIndex)}/mcp/status`
    } else if (trimmedPath.endsWith('/mcp')) {
        parsed.pathname = `${trimmedPath}/status`
    } else {
        return null
    }

    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
}

export function aggregateServerMetrics(snapshot: McpStatusSnapshot): McpServerMetrics {
    const tools = Object.values(snapshot.tools)
    return {
        callCount: tools.reduce((sum, tool) => sum + (tool.Calls ?? tool.calls ?? 0), 0),
        errorCount: tools.reduce((sum, tool) => sum + (tool.Errors ?? tool.errors ?? 0), 0),
        latencyMsP50: tools.reduce((max, tool) => Math.max(max, tool.LatencyP50Ms ?? tool.latencyP50Ms ?? 0), 0),
        latencyMsP99: tools.reduce((max, tool) => Math.max(max, tool.LatencyP99Ms ?? tool.latencyP99Ms ?? 0), 0),
        inflight: snapshot.inflight ?? 0,
        activeSubscriptions: snapshot.activeWorkspaceEventSubscriptions ?? 0,
    }
}

export function extractRecentToolCalls(snapshot: McpStatusSnapshot): McpToolCallEntry[] {
    return (snapshot.recentToolCalls ?? []).map((call) => ({
        toolName: call.toolName ?? 'unknown',
        status: call.status,
        startedAtUtc: call.startedAtUtc,
        completedAtUtc: call.completedAtUtc,
        durationMs: call.durationMs ?? 0,
        errorCode: call.errorCode,
        errorMessage: call.errorMessage,
        requestSummary: call.requestSummary,
        responseSummary: call.responseSummary,
    }))
}

export async function fetchMcpServerStatusDetails(
    server: NormalizedAgentMcpServerConfig,
    fetchImpl: typeof fetch = fetch,
): Promise<McpServerStatusDetails | null> {
    const statusUrl = buildMcpStatusUrl(server)
    const headers = getSecureBlobStatusHeaders(server.headers)
    if (!statusUrl || !headers) return null

    try {
        const response = await fetchImpl.call(globalThis, statusUrl, {
            headers,
        })
        if (response.status === 429) return null
        if (!response.ok) return null

        const snapshot = await response.json() as McpStatusSnapshot
        return {
            metrics: aggregateServerMetrics(snapshot),
            recentToolCalls: extractRecentToolCalls(snapshot),
        }
    } catch {
        return null
    }
}

export async function fetchMcpServerStatus(
    server: NormalizedAgentMcpServerConfig,
    fetchImpl: typeof fetch = fetch,
): Promise<McpServerMetrics | null> {
    const details = await fetchMcpServerStatusDetails(server, fetchImpl)
    return details?.metrics ?? null
}
